import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { useMemo, VFC } from "react";
import styled from "styled-components";
import { Markdown, Select } from "components";
import { useFormField, useAnchor } from "hooks";
import { Features4Content } from "types/graphql/generated";
import { PartnerInfo } from "./components";
import { Partners } from "./components/Partners";

interface Props {
  content: Features4Content;
}

const Features4: VFC<Props> = withErrorBoundary(
  ({
    content: {
      title,
      description,
      features4PremierPartners,
      features4OtherPartners,
      selectLabel,
      mobileSelectPlaceholder,
    },
  }) => {
    const allPartners = useMemo(
      () => [...features4PremierPartners, ...features4OtherPartners],
      [features4PremierPartners, features4OtherPartners]
    );

    const select = useFormField<HTMLSelectElement>();

    const selectedPartner = useMemo(() => {
      return allPartners.find((partner) => partner.partnerName === select.value);
    }, [allPartners, select.value]);

    useAnchor("features4");

    return (
      <StyledFeatures4>
        <div className="features4 Content">
          <h2 className="Features4__title Color_primary Font_48_54_600 lg:Font_40_48_600">{title}</h2>
          <Markdown className="Features4__description Color_secondary Font_24_32_450">{description}</Markdown>
          <Partners content={{ partners: features4PremierPartners }} className="Features4__premierPartners" />
          <Partners content={{ partners: features4OtherPartners }} className="Features4__otherPartners" />
          <div className="Features4__mobileContent">
            <label htmlFor="selectPartner" className="Font_18_26_700">
              {selectLabel}
            </label>
            <Select
              value={select.value}
              onChange={select.onChange}
              className="Features4__partnerSelector"
              id="selectPartner"
            >
              <option value="" disabled>
                {mobileSelectPlaceholder}
              </option>
              {allPartners.map(({ partnerName }) => (
                <option key={partnerName} value={partnerName}>
                  {partnerName}
                </option>
              ))}
            </Select>
            {selectedPartner && <PartnerInfo content={selectedPartner} className="Features4__partnerInfo" />}
          </div>
        </div>
      </StyledFeatures4>
    );
  },
  {}
);

const StyledFeatures4 = styled.section`
  background-color: ${({ theme }) => theme.colors.coral["05"]};
  margin-top: -64px;
  padding-top: 64px;

  .Features4__title {
    max-width: 900px;
  }

  .Features4__description {
    max-width: 871px;
    margin-top: 24px;
  }

  .Features4__premierPartners {
    margin-top: 64px;
  }

  .Features4__otherPartners {
    margin-top: 24px;
  }

  .Features4__mobileContent {
    display: none;
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.lg}px`}) {
    .Features4__title {
      max-width: 582px;
    }

    .Features4__description {
      max-width: 100%;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.md}px`}) {
    background-color: ${({ theme }) => theme.colors.fog["00"]};

    .Features4__title {
      max-width: 500px;
    }

    .Features4__description {
      max-width: 500px;
    }

    .Features4__premierPartners,
    .Features4__otherPartners {
      display: none;
    }

    .Features4__mobileContent {
      display: flex;
      flex-direction: column;
      margin-top: 32px;
    }

    .Features4__partnerSelector {
      max-width: 400px;
      margin-top: 16px;
    }

    .Features4__partnerInfo {
      margin-top: 24px;
    }
  }
`;

gql`
  fragment Features4Content on ComponentFeaturesFeatures4 {
    title
    description
    features4PremierPartners {
      ...Features4PartnersContent
    }
    features4OtherPartners {
      ...Features4PartnersContent
    }
    selectLabel
    mobileSelectPlaceholder
  }
`;

export { Features4 };
